import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import iziToast from "izitoast";
import { registerCompany } from "actions/companyActions";
import Semi360Logo from "components/Semi360Logo";
import "./LoginPage.css";

const Svg = ({ d, size = 18, fill = "none", stroke = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill={fill}
    stroke={stroke}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flex: "0 0 auto" }}
  >
    {d}
  </svg>
);
const IconUser = (p) => (
  <Svg {...p} d={<><circle cx="10" cy="7" r="3" /><path d="M4 17c0-3.5 2.5-6 6-6s6 2.5 6 6" /></>} />
);
const IconMail = (p) => (
  <Svg {...p} d={<><rect x="3" y="5" width="14" height="10" rx="1.5" /><path d="m3.5 6 6.5 5 6.5-5" /></>} />
);
const IconLock = (p) => (
  <Svg {...p} d={<><rect x="4.5" y="9" width="11" height="8" rx="1.5" /><path d="M7 9V6.5a3 3 0 1 1 6 0V9" /></>} />
);
const IconEye = (p) => (
  <Svg {...p} d={<><path d="M2.5 10S5 4.5 10 4.5 17.5 10 17.5 10 15 15.5 10 15.5 2.5 10 2.5 10Z" /><circle cx="10" cy="10" r="2.5" /></>} />
);
const IconEyeOff = (p) => (
  <Svg {...p} d={<><path d="M4 4.5 16 16M7.2 7.4A8.7 8.7 0 0 0 2.5 10s2.5 5.5 7.5 5.5a8.3 8.3 0 0 0 3.5-.78M12.5 12.5a2.5 2.5 0 0 1-3.5-3.5M9.2 5.6A8.9 8.9 0 0 1 10 4.5C15 4.5 17.5 10 17.5 10a13.7 13.7 0 0 1-2.4 2.9" /></>} />
);
const IconPhone = (p) => (
  <Svg {...p} d={<path d="M4 4h3l2 4-2.5 1.5a9 9 0 0 0 5 5L13 12l4 2v3a1.5 1.5 0 0 1-1.5 1.5A13 13 0 0 1 3 5.5 1.5 1.5 0 0 1 4 4Z" />} />
);
const IconBuilding = (p) => (
  <Svg {...p} d={<><rect x="4" y="3" width="12" height="14" rx="1" /><path d="M7 7h2M11 7h2M7 10h2M11 10h2M7 13h2M11 13h2" /></>} />
);
const IconDoc = (p) => (
  <Svg {...p} d={<><path d="M5 3h7l3 3v11H5V3Z" /><path d="M12 3v3h3M8 9h5M8 12h5" /></>} />
);
const IconAlert = (p) => (
  <Svg {...p} size={14} d={<><circle cx="10" cy="10" r="7" /><path d="M10 6.5v4M10 13.25v.01" /></>} />
);
const IconCheck = (p) => <Svg {...p} d={<path d="m4.5 10.5 3.5 3.5 7.5-8" />} />;
const IconArrow = (p) => <Svg {...p} d={<path d="M4 10h12m-4-4 4 4-4 4" />} />;

const Glyph = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
    fill="#24D0C0"
    aria-hidden="true"
  >
    <path d="M100 0C44.7 0 0 44.7 0 100s44.7 100 100 100 100-44.7 100-100S155.3 0 100 0Zm0 134.6c-19.1 0-34.6-15.6-34.6-34.6S80.9 65.4 100 65.4s34.6 15.6 34.6 34.6-15.5 34.6-34.6 34.6Zm53.3-34.6c0-29.5-23.9-53.4-53.3-53.4-19.6 0-36.8 10.7-46 26.5 5.6-27.8 30.2-48.7 59.5-48.7 33.5 0 60.7 27.3 60.7 60.9 0 27.4-18.1 50.6-43 58.2 13.4-9.7 22.1-25.5 22.1-43.5Zm-53.3 81.7c-44.7 0-81.1-36.5-81.1-81.7 0-27.2 13.4-51.3 33.8-66.1-11.6 13.9-18.6 31.7-18.6 51.2 0 44.1 35.5 79.7 79.4 79.7 22.2 0 42.4-9.2 56.7-23.9-14.1 24.2-40.3 40.8-70.2 40.8Z" />
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const validateField = (key, value) => {
  const val = value || "";
  if (key === "name" && !val.trim()) return "Name is required";
  if (key === "email") {
    if (!val.trim()) return "Email is required";
    if (!EMAIL_RE.test(val)) return "Email is invalid";
  }
  if (key === "password" && !val) return "Password is required";
  if (key === "companyName" && !val.trim()) return "Company Name is required";
  if (key === "mobile") {
    if (!val.trim()) return "Mobile number is required";
    if (!/^\d{10}$/.test(val)) return "Mobile number must be 10 digits.";
  }
  // gstin is optional
  return "";
};

const Field = ({ id, label, error, hint, optional, children }) => (
  <div className="sb-field">
    <label htmlFor={id}>
      {label}
      {optional && (
        <span style={{ color: "var(--sb-fg-3)", fontWeight: 400, marginLeft: 6 }}>· optional</span>
      )}
    </label>
    {children}
    {error && (
      <span className="sb-error-text">
        <IconAlert /> {error}
      </span>
    )}
    {!error && hint && (
      <span style={{ font: "400 11px/14px var(--sb-font-ui)", color: "var(--sb-fg-3)" }}>{hint}</span>
    )}
  </div>
);

const RegisterPage = () => {
  const navigate = useNavigate();

  const [theme] = useState("dark");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    companyName: "",
    mobile: "",
    gstin: "",
  });
  const [touched, setTouched] = useState({});
  const [focus, setFocus] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const nameRef = useRef(null);
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const markTouched = (key) => setTouched((prev) => ({ ...prev, [key]: true }));

  const err = (key) => (touched[key] ? validateField(key, form[key]) : "");
  const allValid = ["name", "email", "password", "companyName", "mobile"].every(
    (k) => !validateField(k, form[k]),
  );

  const wrap = (key) =>
    `sb-input-wrap ${focus[key] ? "sb-focused" : ""} ${err(key) ? "sb-error" : ""}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      name: true,
      email: true,
      password: true,
      companyName: true,
      mobile: true,
      gstin: true,
    });
    if (!allValid) return;

    try {
      setIsSaving(true);
      const payload = { ...form, company_name: form.companyName };
      const response = await registerCompany(payload);
      if (response.status) {
        iziToast.success({
          title: "Success",
          message: response.message,
          position: "bottomCenter",
        });
        // Show the "verify your email" success screen instead of redirecting.
        setSuccess(true);
      } else {
        iziToast.error({
          title: "Error",
          message: response.message,
          position: "bottomCenter",
        });
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setIsSaving(false);
    }
  };

  const isDark = theme === "dark";

  return (
    <div
      className={`sb-login-stage ${isDark ? "sb-dark" : "sb-light"}`}
      data-screen-label="Register"
    >
      <Glyph className="sb-glyph" />

      <div className="sb-shell" style={{ maxWidth: 540 }}>
        <div className="sb-brand-lockup">
          <Semi360Logo className="sb-brand-logo" />
        </div>

        {success ? (
          <div className="sb-card" style={{ textAlign: "center" }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: "4px auto 14px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--sb-success-bg, rgba(36, 208, 192, 0.12))",
                color: "var(--sb-success, #1aa899)",
              }}
            >
              <IconMail size={26} />
            </div>
            <h1 style={{ fontSize: 24, lineHeight: "30px" }}>Check your email</h1>
            <p className="sb-lede" style={{ marginTop: 8 }}>
              We've sent a verification link to{" "}
              <strong>{form.email || "your email"}</strong>. Click it to verify your account,
              then sign in. If you don't see it, check your spam folder.
            </p>
            <button
              type="button"
              className="sb-btn sb-btn-primary"
              style={{ marginTop: 8 }}
              onClick={() => navigate("/")}
            >
              Back to login <IconArrow size={16} />
            </button>
          </div>
        ) : (
        <form className="sb-card" onSubmit={handleSubmit} noValidate>
          <p className="sb-eyebrow">Buyer portal</p>
          <h1 style={{ fontSize: 26, lineHeight: "32px" }}>Create your account</h1>
          <p className="sb-lede">
            Sign up to upload BOMs and compare distributor pricing for your team.
          </p>

          {/* Name + Email — give the email column extra width so long addresses aren't clipped */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.5fr)", gap: 14 }}>
            <Field id="r-name" label="Full name" error={err("name")}>
              <div className={wrap("name")}>
                <span className="sb-lead">
                  <IconUser />
                </span>
                <input
                  ref={nameRef}
                  id="r-name"
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Jiya Sharma"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  onFocus={() => setFocus((f) => ({ ...f, name: true }))}
                  onBlur={() => {
                    setFocus((f) => ({ ...f, name: false }));
                    markTouched("name");
                  }}
                  aria-invalid={!!err("name")}
                />
              </div>
            </Field>

            <Field id="r-email" label="Work email" error={err("email")}>
              <div className={wrap("email")}>
                <span className="sb-lead">
                  <IconMail />
                </span>
                <input
                  id="r-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  onFocus={() => setFocus((f) => ({ ...f, email: true }))}
                  onBlur={() => {
                    setFocus((f) => ({ ...f, email: false }));
                    markTouched("email");
                  }}
                  aria-invalid={!!err("email")}
                />
              </div>
            </Field>
          </div>

          {/* Password */}
          <Field id="r-password" label="Password" error={err("password")}>
            <div className={wrap("password")}>
              <span className="sb-lead">
                <IconLock />
              </span>
              <input
                id="r-password"
                type={showPw ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                placeholder="Enter your password"
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                onFocus={() => setFocus((f) => ({ ...f, password: true }))}
                onBlur={() => {
                  setFocus((f) => ({ ...f, password: false }));
                  markTouched("password");
                }}
                aria-invalid={!!err("password")}
              />
              <button
                type="button"
                className="sb-trail"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPw ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
          </Field>

          {/* Company info section divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 -4px" }}>
            <span
              style={{
                font: "600 12px/16px var(--sb-font-ui)",
                color: "var(--sb-fg-2)",
                textTransform: "uppercase",
                letterSpacing: ".12em",
                whiteSpace: "nowrap",
              }}
            >
              Company info
            </span>
            <span style={{ flex: 1, height: 1, background: "var(--sb-stroke-1)" }} />
          </div>

          <Field id="r-company" label="Company name" error={err("companyName")}>
            <div className={wrap("companyName")}>
              <span className="sb-lead">
                <IconBuilding />
              </span>
              <input
                id="r-company"
                type="text"
                name="companyName"
                autoComplete="organization"
                placeholder="Acme Robotics Inc."
                value={form.companyName}
                onChange={(e) => setField("companyName", e.target.value)}
                onFocus={() => setFocus((f) => ({ ...f, companyName: true }))}
                onBlur={() => {
                  setFocus((f) => ({ ...f, companyName: false }));
                  markTouched("companyName");
                }}
                aria-invalid={!!err("companyName")}
              />
            </div>
          </Field>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 14 }}>
            <Field id="r-mobile" label="Mobile" error={err("mobile")} hint="10 digits, no spaces or +91.">
              <div className={wrap("mobile")}>
                <span className="sb-lead">
                  <IconPhone />
                </span>
                <input
                  id="r-mobile"
                  type="tel"
                  name="mobile"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  autoComplete="tel"
                  placeholder="9876543210"
                  value={form.mobile}
                  onChange={(e) =>
                    setField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  onFocus={() => setFocus((f) => ({ ...f, mobile: true }))}
                  onBlur={() => {
                    setFocus((f) => ({ ...f, mobile: false }));
                    markTouched("mobile");
                  }}
                  aria-invalid={!!err("mobile")}
                />
              </div>
            </Field>

            <Field id="r-gstin" label="GSTIN" optional error={err("gstin")}>
              <div className={wrap("gstin")}>
                <span className="sb-lead">
                  <IconDoc />
                </span>
                <input
                  id="r-gstin"
                  type="text"
                  name="gstin"
                  placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                  value={form.gstin}
                  onChange={(e) => setField("gstin", e.target.value.toUpperCase().slice(0, 15))}
                  onFocus={() => setFocus((f) => ({ ...f, gstin: true }))}
                  onBlur={() => {
                    setFocus((f) => ({ ...f, gstin: false }));
                    markTouched("gstin");
                  }}
                  aria-invalid={!!err("gstin")}
                  style={{ fontFamily: "var(--sb-font-mono, monospace)", letterSpacing: ".02em" }}
                />
              </div>
            </Field>
          </div>

          <button
            type="submit"
            className={`sb-btn sb-btn-primary ${success ? "sb-success" : ""}`}
            disabled={isSaving || success}
            style={{ marginTop: 4 }}
          >
            {success ? (
              <>
                <IconCheck size={18} /> Account created
              </>
            ) : isSaving ? (
              <>
                <span className="sb-spinner" /> Creating account…
              </>
            ) : (
              <>
                Create account <IconArrow size={16} />
              </>
            )}
          </button>

          <div className="sb-signup-line">
            Already have an account?{" "}
            <Link to="/" className="sb-link">
              Sign in
            </Link>
          </div>
        </form>
        )}

        <div className="sb-legal">
          By creating an account you agree to our{" "}
          <a href="#" onClick={(e) => e.preventDefault()}>
            Terms
          </a>{" "}
          and{" "}
          <a href="#" onClick={(e) => e.preventDefault()}>
            Privacy
          </a>
          .
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
